import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAppConfig } from '../state/config';
import { useCoreOnly } from '../state/coreOnly';
import { useGeoFilter } from '../state/geoFilter';
import { useSessionUser } from '../state/session';
import { useSyncController } from '../state/sync';
import type { DataMode } from '../types';
import { GEO_ALL } from '../types';

import { Modal } from './Modal';
import { SyncProgressModal } from './SyncProgressModal';

export function TopBar({ onSignOut }: { onSignOut: () => Promise<void> }) {
  const cfg = useAppConfig();
  const user = useSessionUser();
  const { geo, setGeo, unreviewedOnly, setUnreviewedOnly } = useGeoFilter(undefined);
  const { coreOnly, setCoreOnly } = useCoreOnly();
  const sync = useSyncController();
  const [progressOpen, setProgressOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const status = sync.status;
  const running = sync.job?.status === 'running';
  const mode: DataMode = status?.data_mode ?? cfg.data_mode;
  const hasSnapshot = status?.has_snapshot ?? !!cfg.snapshot.last_sync_at;
  const canSwitchOffline = hasSnapshot;
  const specificGeoSelected = geo !== GEO_ALL;
  const isAdmin = user.role === 'admin' || user.role === 'superuser';

  // Auto-open progress modal when a sync starts; user can close at any time.
  useEffect(() => {
    if (running) setProgressOpen(true);
  }, [running]);

  return (
    <header className="sticky top-0 z-30 bg-brand-danone text-white shadow-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-3 whitespace-nowrap">
        <Link
          to="/"
          className="flex items-center gap-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 rounded-md"
        >
          <div className="w-10 h-10 rounded-lg bg-white text-brand-danone font-extrabold flex items-center justify-center text-lg shadow-sm">
            IF
          </div>
          <div className="leading-tight">
            <div className="text-lg font-bold">Intake Forms Assistant</div>
            <div className="text-[11px] text-white/70 uppercase tracking-wide">
              Design Repository &mdash; KB0053029
            </div>
          </div>
        </Link>

        <div className="flex-1" />

        {/* ─── filters (geo first, then unreviewed) ─── */}
        <div className="flex items-center gap-2">
          <select
            aria-label="Geography"
            className="h-9 rounded-md bg-white text-slate-800 text-sm border border-white/10 px-2.5 pr-8 focus:outline-none focus:ring-2 focus:ring-white/70"
            value={geo}
            onChange={(e) => setGeo(e.target.value)}
          >
            <option value={GEO_ALL}>All geographies</option>
            {cfg.geographies.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>

          <label
            className="flex items-center gap-1.5 text-sm select-none pl-1 text-white"
            title={
              specificGeoSelected
                ? 'Show only forms still pending review in the selected geography'
                : 'Show only forms with at least one geography still pending review'
            }
          >
            <input
              type="checkbox"
              className="rounded border-white/60 text-brand-danone focus:ring-white/70"
              checked={unreviewedOnly}
              onChange={(e) => setUnreviewedOnly(e.target.checked)}
            />
            Pending review only
          </label>

          <label
            className="flex items-center gap-1.5 text-sm select-none pl-1 text-white"
            title="Hide local/non-core record producers (those not documented in KB0053029)"
          >
            <input
              type="checkbox"
              className="rounded border-white/60 text-brand-danone focus:ring-white/70"
              checked={coreOnly}
              onChange={(e) => setCoreOnly(e.target.checked)}
            />
            Core forms only
          </label>
        </div>

        {isAdmin && (
          <>
            <div className="h-6 w-px bg-white/25 mx-1" />

            {/* ─── sync + mode (admin only) ─── */}
            <div className="flex items-center gap-2">
              <button
                className="h-9 inline-flex items-center justify-center gap-1.5 rounded-md bg-white text-brand-danone text-sm font-semibold px-4 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                disabled={mode === 'offline' && !running}
                onClick={() => {
                  if (running) {
                    setProgressOpen(true);
                  } else {
                    setConfirmOpen(true);
                  }
                }}
                title={
                  running
                    ? 'View sync progress'
                    : mode === 'offline'
                      ? 'Switch to Online before syncing'
                      : 'Refresh the local offline cache from ServiceNow'
                }
              >
                {running ? 'View progress…' : 'Sync'}
              </button>

              <select
                aria-label="Data mode"
                className="h-9 rounded-md bg-white text-slate-800 text-sm border border-white/10 px-2.5 pr-8 focus:outline-none focus:ring-2 focus:ring-white/70"
                value={mode}
                onChange={(e) => sync.switchMode(e.target.value as DataMode)}
                disabled={running}
              >
                <option value="online">Online</option>
                <option value="offline" disabled={!canSwitchOffline}>
                  Offline{canSwitchOffline ? '' : ' (sync required)'}
                </option>
              </select>

              <SyncStatusLabel />
            </div>
          </>
        )}

        <div className="h-6 w-px bg-white/25 mx-1" />

        <UserMenu fullName={user.full_name} email={user.email} role={user.role} onSignOut={onSignOut} />
      </div>

      {isAdmin && sync.lastError && (
        <div className="bg-rose-50 border-t border-rose-200 text-rose-800 text-sm px-4 py-2">
          Sync error: {sync.lastError}
        </div>
      )}
      {progressOpen && <SyncProgressModal onClose={() => setProgressOpen(false)} />}
      {confirmOpen && (
        <SyncConfirmDialog
          onCancel={() => setConfirmOpen(false)}
          onConfirm={(forceFull) => {
            setConfirmOpen(false);
            void sync.start({ forceFull });
            setProgressOpen(true);
          }}
        />
      )}
    </header>
  );
}

function SyncConfirmDialog({
  onCancel,
  onConfirm,
}: {
  onCancel: () => void;
  onConfirm: (forceFull: boolean) => void;
}) {
  const [forceFull, setForceFull] = useState(false);

  return (
    <Modal title="Run sync" size="md" onClose={onCancel}>
      <div className="space-y-4 text-sm">
        <p className="text-slate-600">
          Refreshes the local snapshot from ServiceNow. By default, tables
          synced in the last 48 hours are reused from cache.
        </p>

        <label className="flex items-start gap-2.5 cursor-pointer select-none">
          <input
            type="checkbox"
            className="mt-0.5 rounded border-slate-300 text-brand-danone focus:ring-brand-danone"
            checked={forceFull}
            onChange={(e) => setForceFull(e.target.checked)}
          />
          <span>
            <span className="font-medium text-slate-900">Force full sync</span>
            <span className="block text-xs text-slate-600">
              Re-fetch every table, ignoring the 48-hour cache. Slower, but
              guarantees the snapshot reflects ServiceNow exactly. Use after
              schema changes or if the last sync looked wrong.
            </span>
          </span>
        </label>

        <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
          <button
            type="button"
            className="btn-ghost h-9 px-4"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="h-9 inline-flex items-center justify-center rounded-md bg-brand-danone text-white text-sm font-semibold px-4 hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-danone/50"
            onClick={() => onConfirm(forceFull)}
          >
            {forceFull ? 'Run full sync' : 'Run sync'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function SyncStatusLabel() {
  const sync = useSyncController();
  const last = sync.status?.last_sync_at;
  if (!last) {
    return <span className="text-xs text-white/70 whitespace-nowrap">Never synced</span>;
  }
  return (
    <span className="text-xs text-white/80 whitespace-nowrap">
      Synced {formatRelative(last)}
    </span>
  );
}

function UserMenu({
  fullName,
  email,
  role,
  onSignOut,
}: {
  fullName: string;
  email: string;
  role: 'lpo' | 'admin' | 'superuser';
  onSignOut: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const initials = fullName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join('') || email.slice(0, 2).toUpperCase();

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        className="w-9 h-9 rounded-full bg-white text-brand-danone text-xs font-bold flex items-center justify-center hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account menu — ${fullName}`}
        title={fullName}
      >
        {initials}
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-1 w-64 rounded-md bg-white shadow-lg border border-slate-200 py-1 z-40"
        >
          <div className="px-3 py-2 border-b border-slate-100">
            <div className="text-sm font-semibold text-slate-900 truncate">{fullName}</div>
            <div className="text-xs text-slate-500 truncate">{email}</div>
            <div className="mt-1 inline-block text-[10px] uppercase tracking-wide bg-slate-100 text-slate-700 rounded px-1.5 py-0.5">
              {role}
            </div>
          </div>
          {role === 'admin' && (
            <Link
              to="/admin/sync"
              role="menuitem"
              className="block px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
              onClick={() => setOpen(false)}
            >
              Sync history
            </Link>
          )}
          <button
            type="button"
            role="menuitem"
            className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
            onClick={() => {
              setOpen(false);
              void onSignOut();
            }}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const diffSec = Math.floor((Date.now() - then) / 1000);
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
}
